// src/services/estoqueService.js
import { doc, getDoc, setDoc, increment, serverTimestamp, collection, query, orderBy, onSnapshot, writeBatch } from "firebase/firestore";
import { db } from "./firebaseConfig";
import { arredondar } from "utils/mathUtils";
import { CNPJ_ECOPARQUE } from "config/constants";

/**
 * Atualiza o estoque no banco de dados com base na leitura de produtos do XML.
 *
 * @param {object} parsedData Objeto completo retornado pelo xmlParser (produtos, tpNF, dhEmi, vNF, chaveAcesso).
 */
export const atualizarEstoqueViaXML = async (parsedData) => {
  try {
    const { produtos, dhEmi, vNF, chaveAcesso, cnpjEmitente, cnpjDestinatario } = parsedData;
    
    // Verificação de segurança: A nota já foi importada?
    if (!chaveAcesso) {
      throw new Error("Chave de acesso ausente no XML.");
    }
    const historicoRef = doc(db, "historico_notas", chaveAcesso);
    const historicoDoc = await getDoc(historicoRef);
    if (historicoDoc.exists()) {
      throw new Error("Esta Nota Fiscal já foi importada anteriormente.");
    }

    // Identificação de Entrada ou Saída com base no CNPJ Emitente
    let tipoOperacao;
    let multiplicador;
    
    if (cnpjEmitente === CNPJ_ECOPARQUE) {
       tipoOperacao = "Saída";
       multiplicador = -1;
    } else {
       tipoOperacao = "Entrada";
       multiplicador = 1;
       
       // Validação de segurança para Entradas
       if (cnpjDestinatario !== CNPJ_ECOPARQUE) {
           throw new Error("A nota foi emitida para outra empresa por engano (CNPJ Destinatário incorreto).");
       }
    }

    let atualizadosCount = 0;

    // NOVO: Verificação de Mapeamento para Entradas
    let pendingMapping = [];
    const produtosMapeados = [];

    if (tipoOperacao === "Entrada") {
      if (!parsedData.cnpjEmitente) {
          throw new Error("CNPJ do emitente não encontrado. Impossível mapear fornecedor.");
      }

      // Buscar os vínculos para cada produto
      for (const produto of produtos) {
          const mappingId = `${parsedData.cnpjEmitente}_${produto.codigo}`;
          const mappingRef = doc(db, "mapeamento_produtos", mappingId);
          const mappingDoc = await getDoc(mappingRef);

          if (mappingDoc.exists()) {
              // Substitui o código do fornecedor pelo nosso código interno para a gravação no estoque
              const interno = mappingDoc.data().codigo_estoque_interno;
              const categoria = mappingDoc.data().categoria_contabil || "N/A";
              produtosMapeados.push({ ...produto, codigoInterno: interno, categoriaContabil: categoria });
          } else {
              // Produto ainda não foi pareado humanamente
              pendingMapping.push({ 
                 codigoFornecedor: produto.codigo, 
                 nomeFornecedor: produto.nome,
                 ncm: produto.ncm,
                 cnpjEmitente: parsedData.cnpjEmitente
              });
          }
      }

      // Se houver pendências, aborta e devolve a lista para o Front-end lidar com o Modal
      if (pendingMapping.length > 0) {
          return {
              success: false,
              pendingMapping: true,
              failedItems: pendingMapping,
              message: "Aguardando intervenção humana para mapeamento de insumos."
          };
      }
    } else {
       // Saídas não exigem mapeamento (assumimos que já usam código interno na emissão)
       for (const produto of produtos) {
           produtosMapeados.push({ ...produto, codigoInterno: produto.codigo, categoriaContabil: "SAIDA_DIRETA" });
       }
    }

    // Inicializa o Batch (operações atômicas)
    const batch = writeBatch(db);

    // AÇÃO A: Preparar atualizações de estoque no batch
    produtosMapeados.forEach((produto) => {
      if (produto.quantidade <= 0) return;

      // REGRA FINANCEIRA: Só atualiza contagem de estoque físico se for "REVENDA" ou Saída
      if (tipoOperacao === "Entrada" && produto.categoriaContabil !== "REVENDA") {
          return; // Pula a alteração no documento "estoque"
      }

      const deltaEstoque = arredondar(produto.quantidade * multiplicador);
      const produtoDocRef = doc(db, "estoque", produto.codigoInterno);

      batch.set(
        produtoDocRef,
        {
          codigo: produto.codigoInterno,
          nome: produto.nome,
          ean: produto.ean || "",
          ncm: produto.ncm || "N/A",
          quantidadeAtual: increment(deltaEstoque),
          ultimaMovimentacao: serverTimestamp(),
        },
        { merge: true }
      );
      atualizadosCount++;
    });

      // AÇÃO B: Criar registro no histórico usando a chave de acesso como ID
    batch.set(historicoRef, {
      chave_acesso: chaveAcesso,
      tipo_operacao: tipoOperacao,
      data_emissao: dhEmi, // String ISO ou vinda do XML
      valor_total: vNF,
      itens: produtosMapeados.map(p => ({
        codigo: p.codigo, // Mantemos o código original do fornecedor no histórico para auditoria
        nome: p.nome,
        quantidade_original: p.quantidade, // Quantidade que de fato veio na NF
        valor_produto: p.vProd || 0, // Valor Monetário
        cfop: p.cfop || "",
        categoria_contabil: p.categoriaContabil
      })),
      data_registro: serverTimestamp()
    });

    // Executar todas as operações de uma vez (All-or-Nothing)
    await batch.commit();

    return { 
        success: true, 
        message: `${atualizadosCount} itens atualizados (Operação de ${tipoOperacao}).`
    };
  } catch (error) {
    console.error("Erro no EstoqueService:", error);
    return { success: false, message: error.message || "Erro de conexão com o banco ao atualizar estoque." };
  }
};

/**
 * Escuta as atualizações da coleção de estoque em tempo real.
 *
 * @param {function} callback Função a ser chamada passando o array de produtos atualizado sempre que o banco mudar.
 * @returns {function} unsubscribe Função para cancelar a escuta (bom para useEffect cleanup).
 */
export const observarEstoque = (callback) => {
  const estoqueRef = collection(db, "estoque");
  const q = query(estoqueRef, orderBy("nome", "asc"));

  // onSnapshot escuta o banco em tempo real
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const lista = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      
      // Formata a data (se existir)
      let dataFormatada = "Data indisponível";
      if (data.ultimaMovimentacao) {
        // ultimaMovimentacao pode ser null se o serverTimestamp acabou de ser disparado e a latência ainda não converteu, 
        // ou um objeto Timestamp. Precisamos lidar com ambos confiantemente.
        const dateObj = data.ultimaMovimentacao.toDate ? data.ultimaMovimentacao.toDate() : new Date();
        dataFormatada = dateObj.toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        });
      }

      lista.push({
        id: docSnap.id, // o id do documento (no caso cProd)
        codigo: data.codigo,
        nome: data.nome,
        ncm: data.ncm || "N/A",
        quantidadeAtual: data.quantidadeAtual || 0,
        ultimaAtualizacao: dataFormatada
      });
    });

    // Chama o callback passando a lista de volta pro componente
    callback(lista);
  }, (error) => {
    console.error("Erro ao escutar o estoque: ", error);
  });

  return unsubscribe;
};

/**
 * Obtém o resumo financeiro das notas no período especificado.
 *
 * @param {number} mes (0-11) Mês do filtro
 * @param {number} ano Ano do filtro
 * @returns {Promise<object>} Objeto com totais { entradas: number, saidas: number }
 */
export const obterResumoFinanceiroEstoque = async (mes, ano) => {
  try {
    const historicoRef = collection(db, "historico_notas");
    
    // Como data_emissao está gravado como string ISO (YYYY-MM-DDTHH:mm:ss.sssZ),
    // podemos filtrar usando strings de começo e fim do mês.
    // Ex: 2023-10-01T00:00:00.000Z até 2023-10-31T23:59:59.999Z
    const startDate = new Date(ano, mes, 1).toISOString();
    const endDate = new Date(ano, mes + 1, 0, 23, 59, 59, 999).toISOString();

    const q = query(
      historicoRef,
      // Usaremos apenas getDocs e filtraremos as datas localmente caso não haja índice criado,
      // para evitar erros de deploy "missing index" imediatos no Firebase. 
      // Em produção massiva, o ideal é compor orderBy e where, exigindo índice.
    );

    const snapshot = await import("firebase/firestore").then(m => m.getDocs(q));

    let totalEntradas = 0;
    let totalSaidas = 0;

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const emissao = data.data_emissao;

      // Filtro local pela data do mês solicitado
      if (emissao >= startDate && emissao <= endDate) {
        if (data.tipo_operacao === "Entrada" || data.tipo_operacao === "0") {
          totalEntradas += (data.valor_total || 0);
        } else if (data.tipo_operacao === "Saída" || data.tipo_operacao === "1") {
          totalSaidas += (data.valor_total || 0);
        }
      }
    });

    return { entradas: totalEntradas, saidas: totalSaidas };
  } catch (error) {
    console.error("Erro ao obter resumo financeiro:", error);
    return { entradas: 0, saidas: 0 };
  }
};

/**
 * Salva a decisão do usuário mapeando o código do produto do fornecedor para um item interno.
 * 
 * @param {string} cnpj CNPJ do fornecedor.
 * @param {string} cProdFornecedor Código original do produto na NFe.
 * @param {string} codigoInterno O ID do documento na coleção `estoque`.
 * @param {string} categoriaContabil A classificação financeira do produto.
 */
export const salvarMapeamento = async (cnpj, cProdFornecedor, codigoInterno, categoriaContabil = "INSUMO") => {
  const mappingId = `${cnpj}_${cProdFornecedor}`;
  const mappingRef = doc(db, "mapeamento_produtos", mappingId);
  
  await setDoc(mappingRef, {
    cnpj_fornecedor: cnpj,
    codigo_fornecedor: cProdFornecedor,
    codigo_estoque_interno: codigoInterno,
    categoria_contabil: categoriaContabil,
    data_mapeamento: serverTimestamp()
  });
};
